import React, { useState } from 'react';
import { X, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { updateAdsRequest } from '../api';

const AdsRequestQuickActionModal = ({ request, onClose, onSuccess, currentUser }) => {
  const { showNotification } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [notes, setNotes] = useState('');

  const actions = [
    { value: 'aprobar', label: 'Aprobar Solicitud', icon: CheckCircle, color: 'green' },
    { value: 'rechazar', label: 'Rechazar Solicitud', icon: XCircle, color: 'red' },
    { value: 'en_proceso', label: 'Marcar En Proceso', icon: Clock, color: 'blue' },
    { value: 'completar', label: 'Marcar como Completado', icon: CheckCircle, color: 'purple' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedAction) {
      showNotification('Por favor selecciona una acción', 'error');
      return;
    }

    setLoading(true);

    try {
      let newEstado = '';
      switch (selectedAction) {
        case 'aprobar':
          newEstado = 'aprobado';
          break;
        case 'rechazar':
          newEstado = 'rechazado';
          break;
        case 'en_proceso':
          newEstado = 'en_proceso';
          break;
        case 'completar':
          newEstado = 'completado';
          break;
        default:
          newEstado = request.estado;
      }

      const dataToSend = {
        ...request,
        estado: newEstado,
        updated_by: currentUser?.name || currentUser?.email || 'Sistema'
      };

      // Si hay notas, agregarlas a la descripción
      if (notes) {
        dataToSend.descripcion = `${request.descripcion}\n\n[Nota de acción rápida]: ${notes}`;
      }

      await updateAdsRequest(request.id, dataToSend);
      
      const actionLabels = {
        aprobar: 'aprobada',
        rechazar: 'rechazada',
        en_proceso: 'marcada en proceso',
        completar: 'completada'
      };
      
      showNotification(`Solicitud ${actionLabels[selectedAction]} exitosamente`, 'success');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      showNotification(error.message || 'Error al procesar la acción', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center rounded-t-lg">
          <h2 className="text-xl font-semibold text-white">Acción Rápida</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Información de la solicitud */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-2">{request.nombre_cliente}</h3>
            <p className="text-sm text-gray-600 mb-2">{request.descripcion}</p>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                request.tipo_solicitud === 'nueva' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {request.tipo_solicitud === 'nueva' ? 'Campaña Nueva' : 'Campaña Actual'}
              </span>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                request.estado === 'pendiente' ? 'bg-yellow-100 text-yellow-800' :
                request.estado === 'en_proceso' ? 'bg-blue-100 text-blue-800' :
                request.estado === 'aprobado' ? 'bg-green-100 text-green-800' :
                request.estado === 'rechazado' ? 'bg-red-100 text-red-800' :
                'bg-purple-100 text-purple-800'
              }`}>
                {request.estado === 'pendiente' ? 'Pendiente' :
                 request.estado === 'en_proceso' ? 'En Proceso' :
                 request.estado === 'aprobado' ? 'Aprobado' :
                 request.estado === 'rechazado' ? 'Rechazado' :
                 'Completado'}
              </span>
            </div>
          </div>

          {/* Selección de acción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selecciona una acción *
            </label>
            <div className="space-y-2">
              {actions.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.value}
                    type="button"
                    onClick={() => setSelectedAction(action.value)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                      selectedAction === action.value
                        ? `border-${action.color}-500 bg-${action.color}-50`
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <Icon 
                      size={20} 
                      className={selectedAction === action.value ? `text-${action.color}-600` : 'text-gray-400'}
                    />
                    <span className={`font-medium ${
                      selectedAction === action.value ? `text-${action.color}-900` : 'text-gray-700'
                    }`}>
                      {action.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notas opcionales */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas (opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Agrega notas sobre esta acción..."
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={loading || !selectedAction}
            >
              {loading ? 'Procesando...' : 'Aplicar Acción'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdsRequestQuickActionModal;
